<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'applyFilter', 'update:modelValue']);
const store = useStore();

const localStatus = ref([]);
const localLabels = ref([]);
const localSources = ref([]);

const availableLabels = computed(() => store.getters['labels/getLabels'] || []);

// Map user's requested options to Chatwoot backend equivalents
const sourceMapping = {
  'Email': 'Channel::Email',
  'Chat button': 'Channel::WebWidget',
  'Contact form': 'Channel::WebWidget',
  'Invitation': 'Channel::WebWidget',
  'Call': 'Channel::Api',
  'Call widget': 'Channel::WebWidget',
  'Facebook': 'Channel::FacebookPage',
  'Facebook message': 'Channel::FacebookPage',
  'Forum': 'Channel::Api',
  'X': 'Channel::TwitterProfile',
  'Suggestion': 'Channel::Api',
  'Instagram': 'Channel::FacebookPage',
  'Instagram mention': 'Channel::FacebookPage',
  'Viber': 'Channel::Api',
  'WhatsApp': 'Channel::Whatsapp',
  'Telegram': 'Channel::Telegram'
};

const allSources = Object.keys(sourceMapping);

const statuses = [
  'All', 'Answered', 'Calling', 'Chatting', 'Spam', 'Deleted', 
  'New', 'Open', 'Resolved', 'Postponed', 'Closed'
];

const applyFilters = () => {
  const payload = [];

  if (localStatus.value.length > 0 && !localStatus.value.includes('All')) {
    // Map custom statuses to Chatwoot standard statuses and custom attributes
    // Since the API strictly uses standard statuses (open, resolved, pending, snoozed, all) 
    // for standard filters, we'll map standard ones.
    const standardStatuses = ['open', 'resolved', 'pending', 'snoozed', 'all'];
    const selectedStandards = localStatus.value.map(s => s.toLowerCase()).filter(s => standardStatuses.includes(s));
    
    if (selectedStandards.length > 0) {
      payload.push({
        attribute_key: 'status',
        filter_operator: 'equal_to',
        values: selectedStandards,
        query_operator: 'and'
      });
    }

    const selectedCustoms = localStatus.value.map(s => s.toLowerCase()).filter(s => !standardStatuses.includes(s));
    if (selectedCustoms.length > 0) {
      payload.push({
        attribute_key: 'call_status',
        custom_attribute_type: 'conversation_attribute',
        filter_operator: 'equal_to',
        values: selectedCustoms,
        query_operator: 'and'
      });
    }
  }

  if (localSources.value.length > 0) {
    const channelTypes = localSources.value.map(s => sourceMapping[s]);
    const inboxes = store.getters['inboxes/getInboxes'] || [];
    const matchedInboxes = inboxes.filter(inbox => channelTypes.includes(inbox.channel_type));
    const inboxIds = matchedInboxes.map(inbox => inbox.id);

    if (inboxIds.length > 0) {
      payload.push({
        attribute_key: 'inbox_id',
        filter_operator: 'equal_to',
        values: inboxIds,
        query_operator: 'and'
      });
    } else {
      payload.push({
        attribute_key: 'inbox_id',
        filter_operator: 'equal_to',
        values: [-1], // Impossible ID to return 0 results
        query_operator: 'and'
      });
    }
  }

  if (localLabels.value.length > 0) {
    payload.push({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: localLabels.value,
      query_operator: 'and'
    });
  }

  emit('update:modelValue', payload);
  emit('applyFilter', payload);
  emit('close');
};
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto pt-24">
    <div class="bg-white dark:bg-n-solid-1 rounded-lg shadow-xl w-full max-w-4xl mx-4 overflow-hidden border border-n-slate-3">
      <div class="flex items-center justify-between p-4 border-b border-n-slate-3 bg-n-slate-1/50 dark:bg-n-solid-2/50">
        <h3 class="text-base font-semibold text-n-slate-12">Tickets - Custom filter</h3>
        <button @click="emit('close')" class="text-n-slate-11 hover:text-n-slate-12 transition-colors">
          <span class="i-ph-x text-xl flex"></span>
        </button>
      </div>
      
      <div class="p-6 space-y-8">
        <!-- Status Filter -->
        <div class="grid grid-cols-12 gap-4 items-start border-b border-n-slate-3 pb-8">
          <div class="col-span-2 text-sm font-medium text-n-slate-12 flex items-center gap-2">
            Status
            <span class="i-ph-copy text-n-slate-10 text-xs"></span>
          </div>
          <div class="col-span-10 flex flex-wrap gap-x-6 gap-y-3">
            <label v-for="st in statuses" :key="st" class="flex items-center gap-2 cursor-pointer group">
              <input type="checkbox" :value="st" v-model="localStatus" class="w-4 h-4 rounded border-n-slate-4 text-[#1f93ff] focus:ring-[#1f93ff]">
              <span class="text-sm text-n-slate-12 group-hover:text-n-slate-11 transition-colors">{{ st }}</span>
            </label>
          </div>
        </div>

        <!-- Tags Filter -->
        <div class="grid grid-cols-12 gap-4 items-start border-b border-n-slate-3 pb-8">
          <div class="col-span-2 text-sm font-medium text-n-slate-12">Tags</div>
          <div class="col-span-10">
            <div class="flex items-center gap-4 mb-4">
              <select class="border border-n-slate-3 rounded px-3 py-1.5 text-sm bg-white dark:bg-n-solid-1 text-n-slate-12 outline-none focus:ring-1 focus:ring-[#1f93ff]">
                <option>contains</option>
              </select>
            </div>
            <div class="flex flex-wrap gap-2">
              <label v-for="label in availableLabels" :key="label.title" class="flex items-center gap-2 cursor-pointer group bg-n-slate-1 dark:bg-n-solid-2 px-3 py-1.5 rounded-full border border-n-slate-3">
                <input type="checkbox" :value="label.title" v-model="localLabels" class="w-4 h-4 rounded border-n-slate-4 text-[#1f93ff] focus:ring-[#1f93ff]">
                <span class="text-xs font-medium text-n-slate-12 group-hover:text-n-slate-11 transition-colors" :style="{ color: label.color }">{{ label.title }}</span>
              </label>
              <div v-if="availableLabels.length === 0" class="text-sm text-n-slate-11">No tags available.</div>
            </div>
          </div>
        </div>

        <!-- Source Filter -->
        <div class="grid grid-cols-12 gap-4 items-start">
          <div class="col-span-2 text-sm font-medium text-n-slate-12 flex items-center gap-2">
            Source
            <span class="i-ph-copy text-n-slate-10 text-xs"></span>
          </div>
          <div class="col-span-10 flex flex-wrap gap-x-6 gap-y-3">
            <label v-for="src in allSources" :key="src" class="flex items-center gap-2 cursor-pointer group">
              <input type="checkbox" :value="src" v-model="localSources" class="w-4 h-4 rounded border-n-slate-4 text-[#1f93ff] focus:ring-[#1f93ff]">
              <span class="text-sm text-n-slate-12 group-hover:text-n-slate-11 transition-colors">{{ src }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="px-6 py-4 bg-n-slate-1/30 dark:bg-n-solid-2/30 border-t border-n-slate-3 flex items-center gap-3">
        <button @click="applyFilters" class="px-5 py-2 bg-[#f2711c] hover:bg-[#e06b1a] text-white rounded font-medium shadow-sm transition-colors text-sm">
          Apply
        </button>
        <button @click="emit('close')" class="px-5 py-2 bg-white dark:bg-n-solid-1 border border-n-slate-4 hover:bg-n-slate-2 text-n-slate-12 rounded font-medium shadow-sm transition-colors text-sm">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>
